import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlus, FaCamera } from 'react-icons/fa6';
import { FaCloudUploadAlt } from 'react-icons/fa';
import { MdClose, MdOutlineUploadFile } from 'react-icons/md';
import { toast } from 'react-toastify';
import ModalLayout from '../layouts/ModalLayout';
import Button from '../buttons/Button';
import CancelSaveButton from '../misc/CancelSaveButton';
import photoAlbumService from '../../../services/community/photoAlbumService';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import { formatBytes } from '../../../utils/formatUtils';

interface AddPhotosModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  albumId: string;
  onPhotosAdded: (photos: Photo[]) => void;
}

export default function AddPhotosModal({ 
  open, 
  setOpen, 
  albumId,
  onPhotosAdded 
}: AddPhotosModalProps): JSX.Element {
  const { t } = useTranslation();
  const [selectedPhotos, setSelectedPhotos] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const community = useSelectedCommunity();

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const validFiles: File[] = [];
    const newPreviews: string[] = [];

    Array.from(files).forEach((file) => {
      if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
        if (file.size <= 20 * 1024 * 1024) { // 20MB limit
          validFiles.push(file);
          newPreviews.push(URL.createObjectURL(file));
        } else {
          toast.error(t('component.modal.addPhotos.uploadTooLarge'));
        }
      } else {
        toast.error(t('component.modal.addPhotos.invalidFileType'));
      }
    });

    if (selectedPhotos.length + validFiles.length > 100) {
      toast.error(t('component.modal.addPhotos.tooManyFiles'));
      return;
    }

    setSelectedPhotos([...selectedPhotos, ...validFiles]);
    setPreviews([...previews, ...newPreviews]);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const removePhoto = (index: number) => {
    const newPhotos = selectedPhotos.filter((_, i) => i !== index);
    const newPreviews = previews.filter((_, i) => i !== index);
    URL.revokeObjectURL(previews[index]);
    
    setSelectedPhotos(newPhotos);
    setPreviews(newPreviews);
  };

  const handleUpload = async () => {
    if (selectedPhotos.length === 0) return;
    if (!community?.id) return;

    setIsUploading(true);
    try {
      const result = await photoAlbumService.addPhotosToAlbum(albumId, selectedPhotos);

      if (result && 'usedStorage' in result && 'maxStorage' in result) {
        toast.error(t('component.modal.addPhotos.limitExceeded', { 
          used: formatBytes(result.usedStorage as number),
          max: formatBytes(result.maxStorage as number)
        }));
        return;
      }

      onPhotosAdded(result);
      toast.success(t('component.modal.addPhotos.uploadSuccess'));

      previews.forEach(preview => URL.revokeObjectURL(preview));
      setSelectedPhotos([]);
      setPreviews([]);
      setOpen(false);
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    previews.forEach(preview => URL.revokeObjectURL(preview));
    setSelectedPhotos([]);
    setPreviews([]);
    setOpen(false);
  };

  return (
    <ModalLayout
      open={open}
      setOpen={setOpen}
      closeButton
      className="z-50 mx-6 w-full max-w-4xl rounded-[12px] bg-secondary-50 p-6"
    >
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-300 bg-opacity-30">
          <FaPlus className="h-6 w-6 text-primary-900" />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-primary-900">
            {t('component.modal.addPhotos.title')}
          </h3>
          <p className="text-sm text-primary-900 text-opacity-80">
            {t('component.modal.addPhotos.subtitle')}
          </p>
        </div>
      </div>

      <div
        className="mb-6 cursor-pointer rounded-[8px] border-2 border-dashed border-primary-300 bg-secondary-200 bg-opacity-30 p-8 transition-colors hover:border-primary hover:bg-opacity-50"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,video/*"
          onChange={handleInputChange}
          className="hidden"
        />
        
        <div className="flex flex-col items-center justify-center text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-300 bg-opacity-30">
            <MdOutlineUploadFile className="h-8 w-8 text-primary-900" />
          </div>
          <h4 className="mb-2 text-lg font-semibold text-primary-900">
            {t('component.modal.addPhotos.uploadArea')}
          </h4>
          <Button variant="secondary" className="gap-2">
            <FaCamera className="h-4 w-4" />
            {t('component.modal.addPhotos.selectFiles')}
          </Button>
        </div>
      </div>

      <div className="mb-6 rounded-lg bg-secondary-200 bg-opacity-50 p-4">
        <div className="flex items-center gap-2 text-sm text-primary-900 text-opacity-80">
          <FaCloudUploadAlt className="h-4 w-4" />
          <span>
            {t('component.modal.addPhotos.uploadInstructions')}
          </span>
        </div>
      </div>

      {selectedPhotos.length > 0 && (
        <div className="mb-6">
          <h4 className="mb-3 font-medium text-primary-900">
            {t('component.modal.addPhotos.selectedPhotos', { count: selectedPhotos.length })}
          </h4>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {previews.map((preview, index) => {
              const file = selectedPhotos[index];
              const isVideo = file.type.startsWith('video/');
              
              return (
                <div key={index} className="group relative aspect-square overflow-hidden rounded-lg">
                  {isVideo ? (
                    <video
                      src={preview}
                      className="h-full w-full object-cover transition-transform group-hover:scale-105"
                      muted
                      preload="metadata"
                    />
                  ) : (
                    <img
                      src={preview}
                      alt={`Preview ${index + 1}`}
                      className="h-full w-full object-cover transition-transform group-hover:scale-105"
                    />
                  )}
                  <div className="absolute inset-0 bg-black bg-opacity-0 transition-opacity group-hover:bg-opacity-30" />
                  
                  {/* Video play indicator */}
                  {isVideo && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="rounded-full bg-black bg-opacity-60 p-2">
                        <svg className="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </div>
                    </div>
                  )}
                  
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removePhoto(index);
                    }}
                    className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-danger bg-opacity-80 text-white opacity-0 transition-opacity group-hover:opacity-100"
                  >
                    <MdClose className="h-4 w-4" />
                  </button>
                  <div className="absolute bottom-2 left-2 rounded bg-black bg-opacity-60 px-2 py-1 text-xs text-white">
                    {file.name.length > 15 
                      ? `${file.name.substring(0, 15)}...` 
                      : file.name}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end">
        <CancelSaveButton
          onCancel={handleCancel}
          onSave={handleUpload}
          disabled={selectedPhotos.length === 0 || isUploading}
          buttonText={isUploading 
            ? t('component.modal.addPhotos.uploading') 
            : t('component.modal.addPhotos.uploadPhotos', { count: selectedPhotos.length })
          }
          cancelText={t('general.cancel')}
        />
      </div>
    </ModalLayout>
  );
}
